import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const sizeClasses = {
  small: 'h-6',
  medium: 'h-8',
  large: 'h-10',
} satisfies Record<string, string>;

type InputGroupProps = HTMLAttributes<HTMLDivElement> & {
  size?: 'small' | 'medium' | 'large';
};

const InputGroup = ({ className, size = 'large', ...props }: InputGroupProps) => {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      role="group"
      className={cn(
        'group/input-group relative flex w-full min-w-0 items-center rounded-xs border border-gray-600 bg-white transition-colors outline-none',
        'hover:border-gray-900',

        // Valid focus
        'has-[[data-slot=input-group-control]:focus:not([aria-invalid=true]):not([data-warning])]:border-green-500',
        'has-[[data-slot=input-group-control]:focus:not([aria-invalid=true]):not([data-warning])]:ring-1',
        'has-[[data-slot=input-group-control]:focus:not([aria-invalid=true]):not([data-warning])]:ring-green-500/50',

        // Invalid
        'has-[[data-slot=input-group-control][aria-invalid=true]]:border-red-400',
        'has-[[data-slot=input-group-control][aria-invalid=true]]:hover:border-red-500',
        'has-[[data-slot=input-group-control][aria-invalid=true]:focus]:border-red-500',
        'has-[[data-slot=input-group-control][aria-invalid=true]:focus]:ring-1',
        'has-[[data-slot=input-group-control][aria-invalid=true]:focus]:ring-red-500/50',

        // Warning
        'has-[[data-slot=input-group-control][data-warning]]:border-yellow-400',
        'has-[[data-slot=input-group-control][data-warning]]:hover:border-yellow-500',
        'has-[[data-slot=input-group-control][data-warning]:focus]:border-yellow-500',
        'has-[[data-slot=input-group-control][data-warning]:focus]:ring-1',
        'has-[[data-slot=input-group-control][data-warning]:focus]:ring-yellow-500/50',

        // Disabled
        'has-[[data-slot=input-group-control]:disabled]:border-gray-400',
        'has-[[data-slot=input-group-control]:disabled]:bg-gray-100',

        // Block/inline layout
        'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col',
        'has-[>textarea]:h-auto',

        // Inner input padding
        'has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
        'has-[>[data-align=inline-end]]:[&>input]:pr-1.5',
        'has-[>[data-align=block-start]]:[&>input]:pb-3',
        'has-[>[data-align=block-end]]:[&>input]:pt-3',
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  );
};

export default InputGroup;
